import React, { useState, useEffect } from 'react';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Badge } from '@/components/ui/Badge';
import { KanbanEtapaModal } from '@/components/KanbanEtapaModal';
import { KanbanEtapa } from '@/types';
import { kanbanService } from '@/services/kanbanService';
import { toast } from 'react-hot-toast';
import { Plus, Edit, Trash2, Eye, EyeOff } from 'lucide-react';

export const KanbanEtapasPage: React.FC = () => {
  const [etapas, setEtapas] = useState<KanbanEtapa[]>([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingEtapa, setEditingEtapa] = useState<KanbanEtapa | null>(null);
  const [isEditing, setIsEditing] = useState(false);

  const loadEtapas = async () => {
    try {
      setLoading(true);
      const data = await kanbanService.getAllKanbans();
      const allEtapas = data.flatMap(kanban => kanban.etapas || []);
      setEtapas(allEtapas);
    } catch (error) {
      toast.error('Erro ao carregar etapas');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadEtapas();
  }, []);

  const handleCreate = () => {
    setEditingEtapa(null);
    setIsEditing(false);
    setModalOpen(true);
  };

  const handleEdit = (etapa: KanbanEtapa) => {
    setEditingEtapa(etapa);
    setIsEditing(true);
    setModalOpen(true);
  };

  const handleSave = async (etapa: KanbanEtapa) => {
    try {
      if (isEditing && editingEtapa) {
        await kanbanService.updateKanbanEtapa(editingEtapa.id, {
          nome: etapa.nome,
          descricao: etapa.descricao || undefined,
          cor: etapa.cor,
          ordem: etapa.ordem,
        });
      } else {
        await kanbanService.createKanbanEtapa({
          kanbanId: etapa.kanbanId,
          nome: etapa.nome,
          descricao: etapa.descricao || undefined,
          cor: etapa.cor,
          ordem: etapa.ordem,
        });
      }
      await loadEtapas();
    } catch (error) {
      toast.error('Erro ao salvar etapa');
    }
  };

  const handleDelete = async (etapa: KanbanEtapa) => {
    if (!confirm(`Tem certeza que deseja excluir a etapa "${etapa.nome}"?`)) {
      return;
    }

    try {
      await kanbanService.deleteKanbanEtapa(etapa.id);
      toast.success('Etapa excluída com sucesso');
      await loadEtapas();
    } catch (error) {
      toast.error('Erro ao excluir etapa');
    }
  };

  const handleToggleStatus = async (etapa: KanbanEtapa) => {
    try {
      await kanbanService.toggleKanbanEtapaStatus(etapa.id);
      toast.success(`Etapa ${etapa.isAtivo ? 'desativada' : 'ativada'} com sucesso`);
      await loadEtapas();
    } catch (error) {
      toast.error('Erro ao alterar status da etapa');
    }
  };

  const columns = [
    {
      key: 'ordem',
      label: 'Ordem',
      render: (etapa: KanbanEtapa) => (
        <span className="font-mono text-sm">{etapa.ordem}</span>
      ),
    },
    {
      key: 'nome',
      label: 'Nome',
      render: (etapa: KanbanEtapa) => (
        <div className="flex items-center space-x-2">
          <div
            className="w-4 h-4 rounded-full border"
            style={{ backgroundColor: etapa.cor }}
          />
          <span className="font-medium">{etapa.nome}</span>
        </div>
      ),
    },
    {
      key: 'descricao',
      label: 'Descrição',
      render: (etapa: KanbanEtapa) => (
        <span className="text-muted-foreground text-sm">
          {etapa.descricao || 'Sem descrição'}
        </span>
      ),
    },
    {
      key: 'status',
      label: 'Status',
      render: (etapa: KanbanEtapa) => (
        <Badge variant={etapa.isAtivo ? 'default' : 'secondary'}>
          {etapa.isAtivo ? 'Ativo' : 'Inativo'}
        </Badge>
      ),
    },
    {
      key: 'titulos',
      label: 'Títulos',
      render: (etapa: KanbanEtapa) => (
        <span className="text-sm text-muted-foreground">
          {etapa._count?.titulos || 0} título(s)
        </span>
      ),
    },
    {
      key: 'actions',
      label: 'Ações',
      render: (etapa: KanbanEtapa) => (
        <div className="flex items-center space-x-2">
          <Button
            size="sm"
            variant="outline"
            onClick={() => handleToggleStatus(etapa)}
            title={etapa.isAtivo ? 'Desativar' : 'Ativar'}
          >
            {etapa.isAtivo ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
          </Button>
          <Button
            size="sm"
            variant="outline"
            onClick={() => handleEdit(etapa)}
            title="Editar"
          >
            <Edit className="w-4 h-4" />
          </Button>
          <Button
            size="sm"
            variant="outline"
            onClick={() => handleDelete(etapa)}
            title="Excluir"
            disabled={(etapa._count?.titulos || 0) > 0}
          >
            <Trash2 className="w-4 h-4" />
          </Button>
        </div>
      ),
    },
  ];

  return (
          <div className="space-y-6" data-testid="page-kanban-etapas">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-foreground">Etapas do Kanban</h1>
          <p className="text-muted-foreground">
            Gerencie as etapas do quadro Kanban para organizar o fluxo de trabalho
          </p>
        </div>
        <Button onClick={handleCreate}>
          <Plus className="w-4 h-4 mr-2" />
          Nova Etapa
        </Button>
      </div>

      <Card>
        <div className="p-6">
          {loading ? (
            <div className="text-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto"></div>
              <p className="mt-2 text-muted-foreground">Carregando etapas...</p>
            </div>
          ) : etapas.length === 0 ? (
            <div className="text-center py-8">
              <p className="text-muted-foreground">Nenhuma etapa encontrada</p>
              <Button onClick={handleCreate} className="mt-4">
                <Plus className="w-4 h-4 mr-2" />
                Criar primeira etapa
              </Button>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-muted/50">
                  <tr>
                    {columns.map((column) => (
                      <th
                        key={column.key}
                        className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider"
                      >
                        {column.label}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {etapas.map((etapa) => (
                    <tr key={etapa.id} className="hover:bg-muted/50">
                      {columns.map((column) => (
                        <td key={column.key} className="px-6 py-4 whitespace-nowrap">
                          {column.render(etapa)}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </Card>

      <KanbanEtapaModal
        isOpen={modalOpen}
        onClose={() => setModalOpen(false)}
        etapa={editingEtapa}
        onSave={handleSave}
        isEditing={isEditing}
      />
    </div>
  );
};
